import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import VipRestriction from '../components/VipRestriction';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';
import {
  Card,
  Button,
  Typography,
  Row,
  Col,
  Space,
  Tag,
  Form,
  Radio,
  Progress,
  message,
  Statistic,
  Steps,
  Timeline,
  Tabs,
  List,
  Avatar,
  Divider,
  Alert,
  Tooltip
} from 'antd';
import {
  ThunderboltOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  HomeOutlined,
  BulbOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  ThunderboltOutlined as BatteryOutlined,
  EyeOutlined,
  HistoryOutlined,
  MedicineBoxOutlined,
  HeartOutlined,
  MessageOutlined,
  CalendarOutlined,
  UserOutlined,
  TeamOutlined,
  CalendarOutlined as CalendarIcon,
  FileTextOutlined as FileIcon,
  HeartOutlined as HeartIcon,
  MessageOutlined as MessageIcon,
  ThunderboltOutlined as EnergyIcon
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { energyConfig } from '../config/energy';

// 配置axios基础URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;
const { TabPane } = Tabs;

const Energy = () => {
  const navigate = useNavigate();
  const { initialize } = useAuthStore();
  const [assessments, setAssessments] = useState([]);
  const [currentAssessment, setCurrentAssessment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [showTipsModal, setShowTipsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedEnergyType, setSelectedEnergyType] = useState('physical');
  const [assessmentForm] = Form.useForm();

  // 获取能量评估记录
  const fetchAssessments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(energyConfig.apiEndpoints.energyAssessments);
      if (response.data.success) {
        setAssessments(response.data.records || []);
      } else {
        message.error(response.data.message || '获取能量评估记录失败');
      }
    } catch (error) {
      console.error('获取能量评估记录失败:', error);
      message.error('获取能量评估记录失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取最新能量指数
  const getLatestEnergyScores = () => {
    const latestAssessments = {};
    energyConfig.energyTypes.forEach(type => {
      const typeAssessments = assessments.filter(a => a.energy_type === type.key);
      if (typeAssessments.length > 0) {
        // 按创建时间排序，取最新的
        const sorted = typeAssessments.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        latestAssessments[type.key] = sorted[0];
      }
    });
    return latestAssessments;
  };

  // 计算能量指数
  const calculateEnergyScore = (answers, questions) => {
    let totalScore = 0;
    let totalWeight = 0;

    questions.forEach(question => {
      const answer = answers[question.id];
      if (answer) {
        const option = question.options.find(opt => opt.value === answer);
        if (option) {
          totalScore += option.score * question.weight;
          totalWeight += question.weight;
        }
      }
    });

    return totalWeight > 0 ? Math.round(totalScore / totalWeight) : 0;
  };

  // 获取问卷问题
  const getQuestions = (energyType) => {
    switch (energyType) {
      case 'physical':
        return energyConfig.physicalEnergyQuestions;
      case 'emotional':
        return energyConfig.emotionalEnergyQuestions;
      case 'mental':
        return energyConfig.mentalEnergyQuestions;
      case 'spiritual':
        return energyConfig.spiritualEnergyQuestions;
      default:
        return [];
    }
  };

  // 开始评估
  const handleStartAssessment = (energyType) => {
    setSelectedEnergyType(energyType);
    assessmentForm.resetFields();
    setShowAssessmentModal(true);
  };

  // 提交评估
  const handleSubmitAssessment = async (values) => {
    try {
      const questions = getQuestions(selectedEnergyType);
      const score = calculateEnergyScore(values, questions);
      
      const requestBody = {
        // user_id 现在从JWT token中获取，不需要在请求中传递
        energy_type: selectedEnergyType,
        answers: values,
        score: score,
        assessment_date: dayjs().format('YYYY-MM-DD')
      };

      const response = await axios.post(energyConfig.apiEndpoints.createAssessment, requestBody);
      if (response.data.success) {
        message.success('评估提交成功');
        setShowAssessmentModal(false);
        fetchAssessments();
      } else {
        message.error(response.data.message || '提交失败');
      }
    } catch (error) {
      console.error('提交评估失败:', error);
      message.error('提交失败');
    }
  };

  // 获取能量等级
  const getEnergyLevel = (score) => {
    if (score >= 71) return { level: 'high', color: 'success', text: '能量充沛' };
    if (score >= 41) return { level: 'medium', color: 'warning', text: '能量一般' };
    return { level: 'low', color: 'error', text: '能量不足' };
  };

  // 获取能量建议
  const getEnergyAdvice = (score) => {
    const level = getEnergyLevel(score);
    const advice = energyConfig.energyAssessmentAdvice.adviceLevels.find(
      a => a.level === level.level
    );
    return advice;
  };

  // 获取最新能量指数
  const latestScores = getLatestEnergyScores();

  // 组件加载时获取数据
  useEffect(() => {
    initialize();
    fetchAssessments();
  }, [initialize]);

  return (
    <VipRestriction>
      <div className="mod-page">
      {/* 页头 */}
      <div className="mod-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/')}
              style={{ marginLeft: '-8px' }}
            >
              返回
            </Button>
            <div className="mod-header-title">
              <ThunderboltOutlined style={{ marginRight: '8px' }} />
              {energyConfig.name}
            </div>
          </div>
          <div className="mod-header-subtitle">
            从身体、情绪、心智、精神四个维度评估你的能量状态，获取专属改善建议
          </div>
        </div>
        <Space wrap>
          <Button
            icon={<HistoryOutlined />}
            onClick={() => setShowHistoryModal(true)}
            style={{ borderColor: energyConfig.bgColor, color: energyConfig.bgColor }}
          >
            评估历史
          </Button>
          <Button
            type="primary"
            icon={<InfoCircleOutlined />}
            onClick={() => setShowTipsModal(true)}
            style={{ backgroundColor: energyConfig.bgColor, borderColor: energyConfig.bgColor }}
          >
            能量TIPS
          </Button>
        </Space>
      </div>

      {/* 主要内容 */}
      <div>

      {/* 能量指数概览 */}
      <div className="mod-section-title">能量指数概览</div>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {energyConfig.energyTypes.map((energyType) => {
          const latestAssessment = latestScores[energyType.key];
          const score = latestAssessment ? latestAssessment.score : 0;
          const level = getEnergyLevel(score);
          
          return (
            <Col xs={24} sm={12} md={6} key={energyType.key}>
              <Card
                hoverable
                style={{ 
                  textAlign: 'center',
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                  border: 'none',
                  transition: 'all 0.3s ease'
                }}
                bodyStyle={{ padding: '6px' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                }}
                actions={[
                  <Button 
                    type="primary" 
                    size="small"
                    onClick={() => handleStartAssessment(energyType.key)}
                    style={{ backgroundColor: energyType.color, borderColor: energyType.color }}
                  >
                    开始评估
                  </Button>
                ]}
              >
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                  {energyType.icon}
                </div>
                <Title level={4} style={{ margin: '8px 0' }}>
                  {energyType.name}
                </Title>
                <div style={{ marginBottom: '8px' }}>
                  <Text type="secondary">{energyType.description}</Text>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <Title level={2} style={{ color: energyType.color, margin: 0 }}>
                    {score}
                  </Title>
                  <Text type="secondary">/ 100</Text>
                </div>
                <Progress 
                  percent={score} 
                  strokeColor={energyType.color}
                  showInfo={false}
                  size="small"
                />
                <div style={{ marginTop: '8px' }}>
                  <Tag color={level.color}>{level.text}</Tag>
                </div>
                {latestAssessment && (
                  <div style={{ marginTop: '8px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      最后评估: {dayjs(latestAssessment.created_at).format('MM-DD')}
                    </Text>
                  </div>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* 能量建议 */}
      <div className="mod-section-title">专属能量建议</div>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        {energyConfig.energyTypes.map((energyType) => {
          const latestAssessment = latestScores[energyType.key];
          const score = latestAssessment ? latestAssessment.score : 0;
          const advice = getEnergyAdvice(score);
          
          return (
            <Col xs={24} sm={12} md={6} key={energyType.key}>
              <Card 
                title={`${energyType.name}建议`} 
                size="small"
                style={{ 
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                  border: 'none',
                  transition: 'all 0.3s ease'
                }}
                bodyStyle={{ padding: '6px' }}
                hoverable
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                }}
              >
                <div style={{ marginBottom: '8px' }}>
                  <Text strong>{advice.title}</Text>
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {advice.description}
                </Text>
                <Divider style={{ margin: '8px 0' }} />
                <List
                  size="small"
                  dataSource={advice.suggestions.slice(0, 2)}
                  renderItem={(item) => (
                    <List.Item style={{ padding: '4px 0' }}>
                      <Text style={{ fontSize: '12px' }}>• {item}</Text>
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* 能量评估弹窗 */}
      {showAssessmentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
                {energyConfig.energyTypes.find(t => t.key === selectedEnergyType)?.name}评估
              </h3>
              <Button 
                type="text" 
                onClick={() => setShowAssessmentModal(false)}
                style={{ fontSize: '18px', width: '24px', height: '24px' }}
              >
                ×
              </Button>
            </div>

            <Form
              form={assessmentForm}
              layout="vertical"
              onFinish={handleSubmitAssessment}
            >
              {getQuestions(selectedEnergyType).map((question, index) => (
                <Form.Item
                  key={question.id}
                  name={question.id}
                  label={
                    <div>
                      <Text strong>{index + 1}. {question.question}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {question.description}
                      </Text>
                    </div>
                  }
                  rules={[{ required: true, message: '请选择答案' }]}
                >
                  <Radio.Group>
                    <Row gutter={[16, 8]}>
                      {question.options.map((option) => (
                        <Col span={24} key={option.value}>
                          <Radio value={option.value}>
                            <div>
                              <Text strong>{option.label}</Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {option.description}
                              </Text>
                            </div>
                          </Radio>
                        </Col>
                      ))}
                    </Row>
                  </Radio.Group>
                </Form.Item>
              ))}
            </Form>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
              paddingTop: '12px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button onClick={() => setShowAssessmentModal(false)}>
                取消
              </Button>
              <Button type="primary" onClick={() => assessmentForm.submit()}>
                提交评估
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 能量TIPS弹窗 */}
      {showTipsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '800px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500', display: 'flex', alignItems: 'center' }}>
                <EnergyIcon style={{ color: energyConfig.bgColor, marginRight: '8px' }} />
                能量管理TIPS
              </h3>
              <Button 
                type="text" 
                onClick={() => setShowTipsModal(false)}
                style={{ fontSize: '18px', width: '24px', height: '24px' }}
              >
                ×
              </Button>
            </div>

            <div style={{ padding: '16px 0' }}>
              {/* 能量管理原则 */}
              <Card title="能量管理四大原则" style={{ marginBottom: '16px' }}>
                <Row gutter={[16, 16]}>
                  {energyConfig.energyManagementTips.principles.map((principle, index) => (
                    <Col xs={24} sm={12} key={index}>
                      <Card size="small" hoverable>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', marginBottom: '8px' }}>
                            {principle.icon}
                          </div>
                          <Title level={5} style={{ margin: '8px 0' }}>
                            {principle.title}
                          </Title>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                            {principle.description}
                          </Text>
                          <List
                            size="small"
                            dataSource={principle.tips}
                            renderItem={(tip) => (
                              <List.Item style={{ padding: '4px 0' }}>
                                <Text style={{ fontSize: '12px' }}>• {tip}</Text>
                              </List.Item>
                            )}
                          />
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card>

              {/* 评估建议 */}
              <Card title="能量评估建议" style={{ marginBottom: '16px' }}>
                <Row gutter={[16, 16]}>
                  {energyConfig.energyAssessmentAdvice.adviceLevels.map((level, index) => (
                    <Col xs={24} sm={8} key={index}>
                      <Card size="small" hoverable>
                        <div style={{ textAlign: 'center' }}>
                          <Title level={5} style={{ margin: '8px 0' }}>
                            {level.title}
                          </Title>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                            {level.description}
                          </Text>
                          <List
                            size="small"
                            dataSource={level.suggestions}
                            renderItem={(suggestion) => (
                              <List.Item style={{ padding: '4px 0' }}>
                                <Text style={{ fontSize: '12px' }}>• {suggestion}</Text>
                              </List.Item>
                            )}
                          />
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card>

              {/* 功能说明 */}
              <Card title="功能模块说明">
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <Title level={5}>💪 身体能量</Title>
                      <Text type="secondary">评估睡眠质量、饮食营养、运动锻炼、身体状态</Text>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <Title level={5}>😊 情绪能量</Title>
                      <Text type="secondary">评估压力水平、情绪稳定性、社交活动、兴趣爱好</Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: '16px' }}>
                      <Title level={5}>🧠 心智能量</Title>
                      <Text type="secondary">评估专注力、学习能力、时间管理、决策能力</Text>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <Title level={5}>✨ 精神能量</Title>
                      <Text type="secondary">评估价值观清晰度、人生目标、创造力、内在动力</Text>
                    </div>
                  </Col>
                </Row>
              </Card>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
              paddingTop: '12px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button onClick={() => setShowTipsModal(false)}>
                关闭
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 评估历史弹窗 */}
      {showHistoryModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '800px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
                评估历史记录
              </h3>
              <Button 
                type="text" 
                onClick={() => setShowHistoryModal(false)}
                style={{ fontSize: '18px', width: '24px', height: '24px' }}
              >
                ×
              </Button>
            </div>

            <Tabs defaultActiveKey="all">
              <TabPane tab="全部" key="all">
                {assessments.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <BatteryOutlined style={{ fontSize: '48px', color: '#D9D9D9', marginBottom: '16px' }} />
                    <Title level={4} style={{ color: '#D9D9D9' }}>暂无评估记录</Title>
                    <Text type="secondary">点击"开始评估"记录您的能量状态</Text>
                  </div>
                ) : (
                  <Row gutter={[16, 16]}>
                    {assessments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((assessment) => {
                      const energyType = energyConfig.energyTypes.find(t => t.key === assessment.energy_type);
                      const level = getEnergyLevel(assessment.score);
                      return (
                        <Col xs={24} sm={12} md={8} lg={6} key={assessment.id}>
                          <Card
                            hoverable
                            style={{ 
                              borderRadius: '16px',
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                              border: 'none',
                              transition: 'all 0.3s ease'
                            }}
                            bodyStyle={{ padding: '6px' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                              e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0) scale(1)';
                              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                            }}
                          >
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '32px', marginBottom: '8px', color: energyType?.color }}>
                                {energyType?.icon}
                              </div>
                              <Title level={5} style={{ margin: '8px 0', fontSize: '14px' }}>
                                {energyType?.name}
                              </Title>
                              <div style={{ marginBottom: '8px' }}>
                                <Tag color={level.color} style={{ fontSize: '10px' }}>
                                  {assessment.score}/100
                                </Tag>
                              </div>
                              <div style={{ marginBottom: '8px' }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  📅 {dayjs(assessment.created_at).format('MM-DD HH:mm')}
                                </Text>
                              </div>
                              <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '8px' }}>
                                {level.text}
                              </Text>
                            </div>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </TabPane>
              {energyConfig.energyTypes.map((energyType) => {
                const typeAssessments = assessments
                  .filter(a => a.energy_type === energyType.key)
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                
                return (
                  <TabPane tab={energyType.name} key={energyType.key}>
                    {typeAssessments.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div style={{ fontSize: '48px', color: '#D9D9D9', marginBottom: '16px' }}>
                          {energyType.icon}
                        </div>
                        <Title level={4} style={{ color: '#D9D9D9' }}>暂无{energyType.name}评估记录</Title>
                        <Text type="secondary">点击"开始评估"记录您的{energyType.name}状态</Text>
                      </div>
                    ) : (
                      <Row gutter={[16, 16]}>
                        {typeAssessments.map((assessment) => {
                          const level = getEnergyLevel(assessment.score);
                          return (
                            <Col xs={24} sm={12} md={8} lg={6} key={assessment.id}>
                              <Card
                                hoverable
                                style={{ 
                                  borderRadius: '16px',
                                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                                  background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                                  border: 'none',
                                  transition: 'all 0.3s ease'
                                }}
                                bodyStyle={{ padding: '6px' }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.2)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                                }}
                              >
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ fontSize: '32px', marginBottom: '8px', color: energyType.color }}>
                                    {energyType.icon}
                                  </div>
                                  <Title level={5} style={{ margin: '8px 0', fontSize: '14px' }}>
                                    {energyType.name}
                                  </Title>
                                  <div style={{ marginBottom: '8px' }}>
                                    <Tag color={level.color} style={{ fontSize: '10px' }}>
                                      {assessment.score}/100
                                    </Tag>
                                  </div>
                                  <div style={{ marginBottom: '8px' }}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                      📅 {dayjs(assessment.created_at).format('MM-DD HH:mm')}
                                    </Text>
                                  </div>
                                  <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '8px' }}>
                                    {level.text}
                                  </Text>
                                </div>
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                    )}
                  </TabPane>
                );
              })}
            </Tabs>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
              paddingTop: '12px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button onClick={() => setShowHistoryModal(false)}>
                关闭
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
      </div>
    </VipRestriction>
  );
};

export default Energy;
